/*
 * Copyright (c) 2018-2023 MemberCenter, a subsidiary of Cameron Collective
 *   All Rights Reserved
 */
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Fab from '@mui/material/Fab';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { ConfirmDeleteDialog } from 'components';
import { useState } from 'react';
import { useDeleteMemberTypeMutation, useGetMemberTypesQuery } from 'store/api/version-api.slice';

import { MemberTypeDialog } from './components';
import { styles } from './styles';

const MemberTypesView = () => {
	const [activeMemberType, setActiveMemberType] = useState(null);

	const [deleteMemberType] = useDeleteMemberTypeMutation();
	const { data = {} } = useGetMemberTypesQuery();

	const memberTypes = data.results || [];

	const [dialogState, setDialogState] = useState({
		edit: false,
		delete: false,
		add: false
	});

	const handleDialogToggle = type => {
		setDialogState({
			...dialogState,
			[type]: !dialogState[type]
		});
	};

	const handleDialogClose = () => {
		setDialogState({
			edit: false,
			add: false,
			delete: false
		});
	};

	const handleDeleteMemberType = async () => {
		await deleteMemberType(activeMemberType.id);

		handleDialogClose();
	};

	const openAddMemberTypeDialog = () => handleDialogToggle('add');
	const openEditMemberTypeDialog = type => () => {
		setActiveMemberType(type);
		handleDialogToggle('edit');
	};

	const openDeleteMemberTypeDialog = type => () => {
		setActiveMemberType(type);
		handleDialogToggle('delete');
	};

	return (
		<Box sx={styles.root}>
			<Box>
				<Paper elevation={2}>
					<Toolbar>
						<Typography component={'span'} variant="h6" color="primary" sx={styles.title}>
							MEMBER TYPES
						</Typography>
						<Fab size="small" color="primary" onClick={openAddMemberTypeDialog}>
							<AddIcon />
						</Fab>
					</Toolbar>
					<Divider />
					<List>
						{memberTypes.map((type, index) => (
							<ListItem key={`member-type-${index}`}>
								<ListItemText primary={type.member_type} secondary={`${type.amount} ${type.billing}`} />
								<ListItemSecondaryAction>
									<IconButton onClick={openEditMemberTypeDialog(type)}>
										<EditIcon />
									</IconButton>
									<IconButton onClick={openDeleteMemberTypeDialog(type)}>
										<DeleteIcon />
									</IconButton>
								</ListItemSecondaryAction>
							</ListItem>
						))}
					</List>
				</Paper>
				<ConfirmDeleteDialog
					open={dialogState.delete}
					onClose={handleDialogClose}
					title={'Delete Member Type?'}
					content={'This action is irreversible. Are you sure you wish to delete this member type?'}
					handleDeleteItem={handleDeleteMemberType}
				/>
				{dialogState.edit && (
					<MemberTypeDialog open={dialogState.edit} onClose={handleDialogClose} member={activeMemberType} />
				)}
				{dialogState.add && <MemberTypeDialog open={dialogState.add} onClose={handleDialogClose} />}
			</Box>
		</Box>
	);
};

export default MemberTypesView;
